import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../context/LanguageContext';
import { motion } from 'framer-motion';
import { Quote, ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { testimonialsApi } from '../../services/api';

const TestimonialsSection = () => {
  const { t } = useTranslation();
  const { isRTL, currentLang } = useLanguage();
  const [testimonials, setTestimonials] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await testimonialsApi.getAll();
      setTestimonials(response.data);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    } finally {
      setLoading(false);
    }
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  if (loading || testimonials.length === 0) {
    return null;
  }

  const current = testimonials[currentIndex];

  return (
    <section data-testid="testimonials-section" className="section-padding bg-brand-dark text-white grain overflow-hidden">
      <div className="container-custom relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="label-text mb-4 block">{t('testimonials.label')}</span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
            {t('testimonials.title')}
          </h2>
        </motion.div>

        {/* Testimonial Card */}
        <div className="max-w-4xl mx-auto">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: isRTL ? -50 : 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: isRTL ? 50 : -50 }}
            transition={{ duration: 0.5 }}
            className="relative"
            data-testid={`testimonial-${currentIndex}`}
          >
            {/* Quote Icon */}
            <Quote className="text-gold/20 w-24 h-24 absolute -top-4 start-0" />

            <div className="relative z-10 text-center px-8">
              {/* Stars */}
              <div className="flex justify-center gap-1 mb-8">
                {[...Array(current.rating || 5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-gold fill-gold" />
                ))}
              </div>

              {/* Quote */}
              <p className="text-xl md:text-2xl lg:text-3xl leading-relaxed text-white/90 mb-10">
                "{currentLang === 'ar' ? current.content_ar : current.content_en}"
              </p>

              {/* Author */}
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 rounded-full bg-gold/20 flex items-center justify-center text-gold text-2xl font-bold mb-4">
                  {(currentLang === 'ar' ? current.client_name_ar : current.client_name_en).charAt(0)}
                </div>
                <h4 className="font-semibold text-lg">
                  {currentLang === 'ar' ? current.client_name_ar : current.client_name_en}
                </h4>
                <span className="text-gold text-sm">
                  {currentLang === 'ar' ? current.location_ar : current.location_en}
                </span>
              </div>
            </div>
          </motion.div>

          {/* Navigation */}
          <div className="flex justify-center items-center gap-4 mt-12">
            <button
              onClick={prevSlide}
              data-testid="testimonial-prev"
              className="w-12 h-12 border border-white/20 flex items-center justify-center hover:bg-gold hover:border-gold hover:text-charcoal transition-colors"
            >
              {isRTL ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
            </button>
            
            <div className="flex gap-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  data-testid={`testimonial-dot-${index}`}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentIndex ? 'w-8 bg-gold' : 'bg-white/30'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={nextSlide}
              data-testid="testimonial-next"
              className="w-12 h-12 border border-white/20 flex items-center justify-center hover:bg-gold hover:border-gold hover:text-charcoal transition-colors"
            >
              {isRTL ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
